/**
 * LCDetail Component
 *
 * Detail view for a single liquid culture showing full information,
 * timeline events, and lineage (parent/children relationships).
 */

import { useMemo } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { useLiquidCulture } from '@/hooks/useLC'
import LCStatusBadge, { GenerationBadge } from './LCStatusBadge'
import { parseDateString } from '@/utils/dateUtils'
import {
  Beaker,
  Calendar,
  Droplet,
  MapPin,
  Edit,
  GitBranch,
  Clock,
  CheckCircle,
  Circle,
  AlertCircle
} from 'lucide-react'

// Status transitions available from each state
const statusTransitions = {
  incubating: ['ready', 'contaminated'],
  ready: ['in_use', 'contaminated'],
  in_use: ['finished', 'contaminated'],
  finished: [],
  contaminated: []
}

// Status button labels
const statusButtonLabels = {
  ready: 'Mark Ready',
  in_use: 'Start Using',
  finished: 'Mark Finished',
  contaminated: 'Mark Contaminated'
}

/**
 * Format a date string to UK locale
 */
function formatDate(dateStr, options = {}) {
  if (!dateStr) return '-'
  return parseDateString(dateStr).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    ...options
  })
}

/**
 * Event type icon mapping
 */
function EventIcon({ eventType }) {
  const iconMap = {
    inoculation: CheckCircle,
    colonisation_complete: CheckCircle,
    state_change: Circle,
    transfer: GitBranch
  }
  const Icon = iconMap[eventType] || Circle
  return <Icon className="h-4 w-4" />
}

/**
 * Event status colour
 */
function getEventStatusColor(status) {
  switch (status) {
    case 'completed':
      return 'text-emerald-600'
    case 'pending':
      return 'text-amber-600'
    case 'planned':
      return 'text-blue-600'
    case 'skipped':
    case 'cancelled':
      return 'text-gray-400'
    default:
      return 'text-gray-600'
  }
}

export default function LCDetail({
  isOpen,
  onClose,
  liquidCultureId,
  onEdit,
  onStatusChange,
  onRefresh
}) {
  const { liquidCulture, loading, error, refetch } = useLiquidCulture(liquidCultureId)

  const availableTransitions = useMemo(() => {
    if (!liquidCulture) return []
    return statusTransitions[liquidCulture.status] || []
  }, [liquidCulture])

  const daysUntilReady = useMemo(() => {
    if (!liquidCulture?.ready_date) return null
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const ready = new Date(liquidCulture.ready_date)
    ready.setHours(0, 0, 0, 0)
    return Math.ceil((ready - today) / (1000 * 60 * 60 * 24))
  }, [liquidCulture?.ready_date])

  const handleStatusChange = async (newStatus) => {
    if (onStatusChange && liquidCulture) {
      await onStatusChange(liquidCulture, newStatus)
      refetch()
      if (onRefresh) onRefresh()
    }
  }

  if (!isOpen) return null

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-6 h-6 border-2 border-muted-foreground border-t-transparent rounded-full animate-spin" />
            <p className="ml-3 text-muted-foreground">Loading...</p>
          </div>
        ) : error ? (
          <div className="py-8 text-center text-red-600">
            <AlertCircle className="h-8 w-8 mx-auto mb-2" />
            <p>{error}</p>
          </div>
        ) : liquidCulture ? (
          <>
            <DialogHeader>
              <div className="flex items-start justify-between">
                <div>
                  <DialogTitle className="flex items-center gap-2 text-xl">
                    <Beaker className="h-5 w-5" aria-hidden="true" />
                    {liquidCulture.culture_code}
                  </DialogTitle>
                  <p className="text-muted-foreground mt-1">
                    {liquidCulture.strain?.name}
                    {liquidCulture.strain?.scientific_name && (
                      <span className="italic ml-1">
                        ({liquidCulture.strain.scientific_name})
                      </span>
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <GenerationBadge generation={liquidCulture.generation} />
                  <LCStatusBadge status={liquidCulture.status} />
                </div>
              </div>
            </DialogHeader>

            {/* Main Info Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-4">
              <div className="flex items-start gap-2">
                <Calendar className="h-4 w-4 mt-0.5 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">Inoculated</p>
                  <p className="font-medium">{formatDate(liquidCulture.inoculation_date)}</p>
                </div>
              </div>

              <div className="flex items-start gap-2">
                <Clock className="h-4 w-4 mt-0.5 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">Ready Date</p>
                  <p className="font-medium">{formatDate(liquidCulture.ready_date)}</p>
                  {liquidCulture.status === 'incubating' && daysUntilReady !== null && (
                    <p className={`text-xs ${daysUntilReady <= 0 ? 'text-emerald-600' : 'text-muted-foreground'}`}>
                      {daysUntilReady > 0
                        ? `${daysUntilReady} days remaining`
                        : daysUntilReady === 0
                          ? 'Ready today'
                          : `${Math.abs(daysUntilReady)} days overdue`
                      }
                    </p>
                  )}
                </div>
              </div>

              <div className="flex items-start gap-2">
                <Droplet className="h-4 w-4 mt-0.5 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">Volume</p>
                  <p className="font-medium">{liquidCulture.volume_ml} ml</p>
                </div>
              </div>

              {liquidCulture.storage_location && (
                <div className="flex items-start gap-2 col-span-2 sm:col-span-3">
                  <MapPin className="h-4 w-4 mt-0.5 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Storage Location</p>
                    <p className="font-medium">{liquidCulture.storage_location}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Notes */}
            {liquidCulture.notes && (
              <Card className="mt-4 bg-muted/50">
                <CardContent className="py-3">
                  <p className="text-xs text-muted-foreground mb-1">Notes</p>
                  <p className="text-sm">{liquidCulture.notes}</p>
                </CardContent>
              </Card>
            )}

            {/* Lineage Section */}
            <Card className="mt-4">
              <CardHeader className="py-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <GitBranch className="h-4 w-4" />
                  Lineage
                </CardTitle>
              </CardHeader>
              <CardContent className="py-3 pt-0">
                <div className="space-y-3">
                  {/* Parent */}
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Parent Culture</p>
                    {liquidCulture.parent ? (
                      <Badge variant="outline" className="font-mono">
                        {liquidCulture.parent.culture_code} (G{liquidCulture.parent.generation})
                      </Badge>
                    ) : (
                      <p className="text-sm text-muted-foreground">None (Master Culture)</p>
                    )}
                  </div>

                  {/* Children */}
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Child Cultures</p>
                    {liquidCulture.children?.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {liquidCulture.children.map(child => (
                          <Badge
                            key={child.id}
                            variant="outline"
                            className={`font-mono ${child.status === 'contaminated' ? 'opacity-50 line-through' : ''}`}
                          >
                            {child.culture_code} (G{child.generation})
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">No children yet</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Timeline Events */}
            {liquidCulture.timeline_events?.length > 0 && (
              <Card className="mt-4">
                <CardHeader className="py-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Timeline
                  </CardTitle>
                </CardHeader>
                <CardContent className="py-3 pt-0">
                  <div className="space-y-3">
                    {liquidCulture.timeline_events
                      .sort((a, b) => new Date(b.planned_date || b.created_at) - new Date(a.planned_date || a.created_at))
                      .map(event => (
                        <div key={event.id} className="flex items-start gap-3">
                          <div className={getEventStatusColor(event.status)}>
                            <EventIcon eventType={event.event_type} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium">{event.event_description}</p>
                            <div className="flex items-center gap-2 mt-0.5">
                              <p className="text-xs text-muted-foreground">
                                {event.actual_date
                                  ? formatDate(event.actual_date)
                                  : `Planned: ${formatDate(event.planned_date)}`
                                }
                              </p>
                              <Badge variant="outline" className="text-xs py-0">
                                {event.status}
                              </Badge>
                            </div>
                            {event.notes && (
                              <p className="text-xs text-muted-foreground mt-1">{event.notes}</p>
                            )}
                          </div>
                        </div>
                      ))
                    }
                  </div>
                </CardContent>
              </Card>
            )}

            <Separator className="my-4" />

            {/* Actions */}
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                onClick={() => onEdit && onEdit(liquidCulture)}
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Button>

              {availableTransitions.map(status => (
                <Button
                  key={status}
                  variant={status === 'contaminated' ? 'destructive' : 'outline'}
                  onClick={() => handleStatusChange(status)}
                >
                  {statusButtonLabels[status] || `Mark ${status}`}
                </Button>
              ))}
            </div>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  )
}
